import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Video, Users, Calendar, Clock, Plus, Trash2, Edit, 
  Play, Square, Copy, Check, Settings, Radio, X,
  Music, ChevronRight, RefreshCw, Eye, DollarSign, Image, Upload
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, color = "primary" }) => (
  <div className="bg-stone-800/50 rounded-xl p-4 border border-stone-700">
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
        color === 'primary' ? 'bg-primary/20' :
        color === 'green' ? 'bg-green-500/20' :
        color === 'red' ? 'bg-red-500/20' :
        'bg-amber-500/20'
      }`}>
        <Icon className={`w-5 h-5 ${
          color === 'primary' ? 'text-primary' :
          color === 'green' ? 'text-green-500' :
          color === 'red' ? 'text-red-500' :
          'text-amber-500'
        }`} />
      </div>
      <div>
        <p className="text-2xl font-bold text-white">{value}</p>
        <p className="text-sm text-muted-foreground">{label}</p>
      </div>
    </div>
  </div>
);

// Active Session Card
const ActiveSessionCard = ({ session, onEnd, onCopyCode }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-gradient-to-br from-red-500/20 to-amber-500/10 rounded-xl p-4 border border-red-500/30"
  >
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
        <span className="text-red-400 text-sm font-medium">LIVE</span>
      </div>
      <span className="font-mono font-bold text-primary">{session.code}</span>
    </div>
    
    <h3 className="text-lg font-bold text-white mb-2">{session.title}</h3>
    
    <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
      <span className="flex items-center gap-1">
        <Users className="w-4 h-4" />
        {session.participant_count || 0}/{session.max_students}
      </span>
      {session.instrument && (
        <span className="flex items-center gap-1">
          <Music className="w-4 h-4" />
          {session.instrument}
        </span>
      )}
    </div>
    
    <div className="flex gap-2">
      <button
        onClick={() => onCopyCode(session.code)}
        className="flex-1 py-2 bg-stone-700 rounded-lg hover:bg-stone-600 transition-colors flex items-center justify-center gap-2 text-sm"
      >
        <Copy className="w-4 h-4" />
        Copy Code
      </button>
      <button
        onClick={() => onEnd(session.code)}
        className="flex-1 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors flex items-center justify-center gap-2 text-sm"
      >
        <Square className="w-4 h-4" />
        End
      </button>
    </div>
  </motion.div>
);

// Slot Card
const SlotCard = ({ slot, onDelete, onEdit }) => {
  const dateObj = new Date(slot.date + 'T' + slot.time);
  const isPrivate = slot.type === 'private';
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-stone-800/50 rounded-lg p-4 border border-stone-700"
    >
      <div className="flex justify-between items-start mb-2">
        <div>
          <p className="text-sm text-muted-foreground">
            {dateObj.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
          </p>
          <p className="text-xl font-bold text-primary">{slot.time}</p>
        </div>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          isPrivate ? 'bg-amber-500/20 text-amber-400' : 'bg-green-500/20 text-green-400'
        }`}>
          {isPrivate ? '1-on-1' : 'Group'}
        </span>
      </div>
      
      <div className="flex items-center gap-3 text-sm text-muted-foreground mb-3">
        <span>{slot.duration} min</span>
        <span>${slot.price}</span>
        <span>{slot.spots_left}/{slot.max_students} spots</span>
      </div>
      
      <div className="flex gap-2">
        <button
          onClick={() => onEdit(slot)}
          className="flex-1 py-1.5 bg-stone-700 rounded text-sm hover:bg-stone-600 transition-colors"
        >
          Edit
        </button>
        <button
          onClick={() => onDelete(slot.id)}
          className="py-1.5 px-3 bg-red-500/20 text-red-400 rounded text-sm hover:bg-red-500/30 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
};

export default function AdminLiveLessons() {
  const [stats, setStats] = useState({ total_sessions: 0, active_sessions: 0, total_bookings: 0, instruments: 10 });
  const [activeSessions, setActiveSessions] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [instruments, setInstruments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState(null);
  
  // Modals
  const [showCreateSession, setShowCreateSession] = useState(false);
  const [showCreateSlot, setShowCreateSlot] = useState(false);
  const [showEditSlot, setShowEditSlot] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  const [showEditInstrument, setShowEditInstrument] = useState(false);
  const [showAddInstrument, setShowAddInstrument] = useState(false);
  const [editingInstrument, setEditingInstrument] = useState(null);
  
  // Forms
  const [sessionForm, setSessionForm] = useState({
    title: 'Live Samba Lesson',
    instrument: '',
    max_students: 5,
    is_group: true
  });
  
  const [slotForm, setSlotForm] = useState({
    date: new Date().toISOString().split('T')[0],
    time: '10:00',
    duration: 60,
    max_students: 1,
    instrument: '',
    level: 'all',
    price: 50
  });

  const [instrumentForm, setInstrumentForm] = useState({
    id: '',
    name: '',
    image: '',
    description: ''
  });

  // Available instrument photos from user uploads
  const availablePhotos = [
    { name: "Surdo 1", url: "https://www.casasamba.com/wp-content/uploads/2026/09/90obs3y2_Surdo-1.jpg" },
    { name: "Three Surdo", url: "https://www.casasamba.com/wp-content/uploads/2026/09/hvbxzby0_Three-Surdo.jpg" },
    { name: "Surdos 3", url: "https://www.casasamba.com/wp-content/uploads/2026/09/fa6ylgln_Surdos-3.jpg" },
    { name: "Surdo Premeira", url: "https://www.casasamba.com/wp-content/uploads/2026/09/vhol8vbh_Surdo-Premeira.jpg" },
    { name: "Pandeiro", url: "https://www.casasamba.com/wp-content/uploads/2026/09/ea6b06e4_Pandeiro.jpg" },
    { name: "Tambourim", url: "https://www.casasamba.com/wp-content/uploads/2026/09/f1zb9ary_Tambourim.jpg" },
    { name: "Timbau", url: "https://www.casasamba.com/wp-content/uploads/2026/09/rtid2h63_Timbau.jpg" },
    { name: "Caixa Repeque 3", url: "https://www.casasamba.com/wp-content/uploads/2026/09/60pr4zhk_caixa_repeque3.jpg" },
    { name: "Caixa Repeque 8", url: "https://www.casasamba.com/wp-content/uploads/2026/09/73zxmtfj_caixa_repeque8.jpg" },
    { name: "Agogo", url: "https://www.casasamba.com/wp-content/uploads/2026/09/x5ukr87e_Agogo.jpg" },
    { name: "Reco-Reco", url: "https://www.casasamba.com/wp-content/uploads/2026/09/c70m1q7s_Reco-Reco.jpg" },
    { name: "Atabaque", url: "https://www.casasamba.com/wp-content/uploads/2026/09/3l9bbv47_atabaque11-2.jpg" },
    { name: "Cuica", url: "https://www.casasamba.com/wp-content/uploads/2026/09/qg1yvxz2_Cuica.jpg" },
    { name: "Berimbau", url: "https://www.casasamba.com/wp-content/uploads/2026/09/vjfgnz4u_berimbau1.jpg" },
    { name: "Platenella", url: "https://www.casasamba.com/wp-content/uploads/2026/09/fjxklurm_Platenella.jpg" },
    { name: "Frying Pan", url: "https://www.casasamba.com/wp-content/uploads/2026/09/ljjuw152_Frying-pan.jpg" },
    { name: "Samba Instruments", url: "https://www.casasamba.com/wp-content/uploads/2026/09/rldhyyfe_Samba-instruments.jpg" },
    { name: "Pagode Instruments", url: "https://www.casasamba.com/wp-content/uploads/2026/09/k41dpwmt_Poagode-Instruments.jpg" },
  ];

  // Fetch data
  const fetchData = async () => {
    try {
      const [statsRes, activeRes, scheduleRes, instRes] = await Promise.all([
        fetch(`${API}/api/live-lessons/stats`),
        fetch(`${API}/api/live-lessons/sessions/active/list`),
        fetch(`${API}/api/live-lessons/schedule`),
        fetch(`${API}/api/live-lessons/instruments`)
      ]);
      
      if (statsRes.ok) setStats(await statsRes.json());
      if (activeRes.ok) setActiveSessions(await activeRes.json());
      if (scheduleRes.ok) setSchedule(await scheduleRes.json());
      if (instRes.ok) setInstruments(await instRes.json());
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Poll for active sessions
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Create live session
  const handleCreateSession = async () => {
    try {
      const res = await fetch(`${API}/api/live-lessons/sessions/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sessionForm)
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to create session');
      }
      
      toast.success(`Session created! Code: ${data.session_code}`);
      setShowCreateSession(false);
      fetchData();
      
      // Copy code to clipboard
      navigator.clipboard.writeText(data.session_code);
    } catch (err) {
      toast.error(err.message);
    }
  };

  // End session
  const handleEndSession = async (code) => {
    try {
      await fetch(`${API}/api/live-lessons/sessions/${code}/end`, { method: 'POST' });
      toast.success('Session ended');
      fetchData();
    } catch (err) {
      toast.error('Failed to end session');
    }
  };

  // Create slot
  const handleCreateSlot = async () => {
    try {
      const res = await fetch(`${API}/api/live-lessons/schedule/slots`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(slotForm)
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to create slot');
      }
      
      toast.success('Lesson slot created');
      setShowCreateSlot(false);
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Delete slot
  const handleDeleteSlot = async (slotId) => {
    if (!confirm('Are you sure you want to delete this lesson slot?')) return;
    
    try {
      const res = await fetch(`${API}/api/live-lessons/schedule/slots/${slotId}`, {
        method: 'DELETE'
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to delete slot');
      }
      
      toast.success('Lesson slot deleted');
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Edit slot - open modal with existing data
  const openEditSlot = (slot) => {
    setEditingSlot(slot);
    setSlotForm({
      date: slot.date,
      time: slot.time,
      duration: slot.duration,
      max_students: slot.max_students,
      instrument: slot.instrument || '',
      level: slot.level || 'all',
      price: slot.price
    });
    setShowEditSlot(true);
  };

  // Update slot
  const handleUpdateSlot = async () => {
    if (!editingSlot) return;
    
    try {
      const res = await fetch(`${API}/api/live-lessons/schedule/slots/${editingSlot.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(slotForm)
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to update slot');
      }
      
      toast.success('Lesson slot updated');
      setShowEditSlot(false);
      setEditingSlot(null);
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Copy session code
  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success('Code copied!');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  // Join session as host
  const joinAsHost = (code) => {
    window.open(`/play-along?host=true&code=${code}`, '_blank');
  };

  // Open edit instrument modal
  const openEditInstrument = (inst) => {
    setEditingInstrument(inst);
    setInstrumentForm({
      id: inst.id,
      name: inst.name,
      image: inst.image,
      description: inst.description || ''
    });
    setShowEditInstrument(true);
  };

  // Update instrument
  const handleUpdateInstrument = async () => {
    if (!editingInstrument) return;
    
    try {
      // Always send name, image, and description
      const params = new URLSearchParams();
      params.append('name', instrumentForm.name);
      params.append('image', instrumentForm.image);
      params.append('description', instrumentForm.description || '');
      
      const res = await fetch(`${API}/api/live-lessons/instruments/${editingInstrument.id}?${params.toString()}`, {
        method: 'PUT'
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to update instrument');
      }
      
      toast.success('Instrument updated!');
      setShowEditInstrument(false);
      setEditingInstrument(null);
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Add new instrument
  const handleAddInstrument = async () => {
    try {
      const params = new URLSearchParams({
        id: instrumentForm.id.toLowerCase().replace(/\s+/g, '-'),
        name: instrumentForm.name,
        image: instrumentForm.image
      });
      
      const res = await fetch(`${API}/api/live-lessons/instruments?${params.toString()}`, {
        method: 'POST'
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to add instrument');
      }
      
      toast.success('Instrument added');
      setShowAddInstrument(false);
      setInstrumentForm({ id: '', name: '', image: '' });
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Delete instrument
  const handleDeleteInstrument = async (instrumentId) => {
    if (!confirm('Are you sure you want to delete this instrument?')) return;
    
    try {
      const res = await fetch(`${API}/api/live-lessons/instruments/${instrumentId}`, {
        method: 'DELETE'
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to delete instrument');
      }
      
      toast.success('Instrument deleted');
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-900 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-900 text-white p-6" data-testid="admin-live-lessons">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">Live Lessons Admin</h1>
            <p className="text-muted-foreground">Manage sessions and schedule</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowCreateSlot(true)}
              className="px-4 py-2 bg-stone-700 rounded-lg hover:bg-stone-600 transition-colors flex items-center gap-2"
            >
              <Calendar className="w-5 h-5" />
              Add Slot
            </button>
            <button
              onClick={() => setShowCreateSession(true)}
              className="px-4 py-2 bg-primary text-black font-bold rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
            >
              <Radio className="w-5 h-5" />
              Go Live
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Video} label="Total Sessions" value={stats.total_sessions} color="primary" />
          <StatCard icon={Radio} label="Live Now" value={stats.active_sessions} color="red" />
          <StatCard icon={Calendar} label="Bookings" value={stats.total_bookings} color="green" />
          <StatCard icon={Music} label="Instruments" value={stats.instruments} color="amber" />
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Active Sessions */}
          <div className="lg:col-span-1">
            <div className="bg-stone-800 rounded-xl p-4 border border-stone-700">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <Radio className="w-5 h-5 text-red-500" />
                  Live Sessions
                </h2>
                <button onClick={fetchData} className="p-1 hover:bg-stone-700 rounded">
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
              
              {activeSessions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Video className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No active sessions</p>
                  <button
                    onClick={() => setShowCreateSession(true)}
                    className="mt-3 text-primary text-sm hover:underline"
                  >
                    Start a live session
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {activeSessions.map((session) => (
                    <ActiveSessionCard
                      key={session.code}
                      session={session}
                      onEnd={handleEndSession}
                      onCopyCode={copyCode}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Schedule */}
          <div className="lg:col-span-2">
            <div className="bg-stone-800 rounded-xl p-4 border border-stone-700">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  Upcoming Schedule
                </h2>
                <button
                  onClick={() => setShowCreateSlot(true)}
                  className="p-2 bg-stone-700 rounded-lg hover:bg-stone-600 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              
              {schedule.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No scheduled lessons</p>
                  <button
                    onClick={() => setShowCreateSlot(true)}
                    className="mt-3 text-primary text-sm hover:underline"
                  >
                    Create a lesson slot
                  </button>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {schedule.slice(0, 9).map((slot) => (
                    <SlotCard
                      key={slot.id}
                      slot={slot}
                      onDelete={handleDeleteSlot}
                      onEdit={openEditSlot}
                    />
                  ))}
                </div>
              )}
              
              {schedule.length > 9 && (
                <p className="text-center text-sm text-muted-foreground mt-4">
                  +{schedule.length - 9} more slots
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 bg-stone-800 rounded-xl p-6 border border-stone-700">
          <h2 className="text-lg font-bold mb-4">Quick Actions</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <button
              onClick={() => setShowCreateSession(true)}
              className="p-4 bg-gradient-to-br from-primary/20 to-amber-500/10 rounded-xl border border-primary/30 hover:border-primary/50 transition-all text-left"
            >
              <Radio className="w-8 h-8 text-primary mb-2" />
              <h3 className="font-bold">Start Live Session</h3>
              <p className="text-sm text-muted-foreground">Go live now with students</p>
            </button>
            
            <button
              onClick={() => setShowCreateSlot(true)}
              className="p-4 bg-stone-700/50 rounded-xl border border-stone-600 hover:border-stone-500 transition-all text-left"
            >
              <Plus className="w-8 h-8 text-green-500 mb-2" />
              <h3 className="font-bold">Add Lesson Slot</h3>
              <p className="text-sm text-muted-foreground">Schedule a new lesson</p>
            </button>
            
            <button
              onClick={() => window.open('/play-along', '_blank')}
              className="p-4 bg-stone-700/50 rounded-xl border border-stone-600 hover:border-stone-500 transition-all text-left"
            >
              <Eye className="w-8 h-8 text-blue-500 mb-2" />
              <h3 className="font-bold">View Student Page</h3>
              <p className="text-sm text-muted-foreground">See what students see</p>
            </button>
            
            <button
              onClick={fetchData}
              className="p-4 bg-stone-700/50 rounded-xl border border-stone-600 hover:border-stone-500 transition-all text-left"
            >
              <RefreshCw className="w-8 h-8 text-amber-500 mb-2" />
              <h3 className="font-bold">Refresh Data</h3>
              <p className="text-sm text-muted-foreground">Update all statistics</p>
            </button>
          </div>
        </div>

        {/* Instruments Editor */}
        <div className="mt-8 bg-stone-800 rounded-xl p-6 border border-stone-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold flex items-center gap-2">
              <Music className="w-5 h-5 text-primary" />
              Instruments Editor
            </h2>
            <button
              onClick={() => setShowAddInstrument(true)}
              className="px-3 py-1.5 bg-primary text-black font-bold rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2 text-sm"
            >
              <Plus className="w-4 h-4" />
              Add Instrument
            </button>
          </div>
          
          <p className="text-sm text-muted-foreground mb-4">
            Edit instrument images shown on the Live Lessons booking page
          </p>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {instruments.map((inst) => (
              <div
                key={inst.id}
                className="bg-stone-700/50 rounded-xl p-3 border border-stone-600 hover:border-primary/50 transition-all group"
              >
                <div className="relative mb-2">
                  <img
                    src={inst.image}
                    alt={inst.name}
                    className="w-full h-20 object-cover rounded-lg"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/100x100?text=' + inst.name;
                    }}
                  />
                  <button
                    onClick={() => openEditInstrument(inst)}
                    className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center"
                  >
                    <Edit className="w-5 h-5 text-white" />
                  </button>
                </div>
                <p className="text-sm font-medium text-center truncate">{inst.name}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Create Session Modal */}
      <AnimatePresence>
        {showCreateSession && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
            onClick={() => setShowCreateSession(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-stone-800 rounded-2xl p-6 max-w-md w-full border border-stone-700"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <Radio className="w-5 h-5 text-red-500" />
                  Start Live Session
                </h3>
                <button onClick={() => setShowCreateSession(false)} className="p-1 hover:bg-stone-700 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Session Title</label>
                  <input
                    type="text"
                    value={sessionForm.title}
                    onChange={(e) => setSessionForm({ ...sessionForm, title: e.target.value })}
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Instrument Focus</label>
                  <select
                    value={sessionForm.instrument}
                    onChange={(e) => setSessionForm({ ...sessionForm, instrument: e.target.value })}
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none"
                  >
                    <option value="">All Instruments</option>
                    {instruments.map((inst) => (
                      <option key={inst.id} value={inst.name}>{inst.name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Max Students</label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={sessionForm.max_students}
                    onChange={(e) => setSessionForm({ ...sessionForm, max_students: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none"
                  />
                </div>
                
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="is_group"
                    checked={sessionForm.is_group}
                    onChange={(e) => setSessionForm({ ...sessionForm, is_group: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <label htmlFor="is_group" className="text-sm">This is a group session</label>
                </div>
                
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setShowCreateSession(false)}
                    className="flex-1 py-2 bg-stone-700 rounded-lg hover:bg-stone-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateSession}
                    className="flex-1 py-2 bg-red-500 text-white font-bold rounded-lg hover:bg-red-600 transition-colors flex items-center justify-center gap-2"
                  >
                    <Radio className="w-4 h-4" />
                    Go Live
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Slot Modal */}
      <AnimatePresence>
        {showCreateSlot && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
            onClick={() => setShowCreateSlot(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-stone-800 rounded-2xl p-6 max-w-md w-full border border-stone-700"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  Create Lesson Slot
                </h3>
                <button onClick={() => setShowCreateSlot(false)} className="p-1 hover:bg-stone-700 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Date</label>
                    <input
                      type="date"
                      value={slotForm.date}
                      onChange={(e) => setSlotForm({ ...slotForm, date: e.target.value })}
                      className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Time</label>
                    <input
                      type="time"
                      value={slotForm.time}
                      onChange={(e) => setSlotForm({ ...slotForm, time: e.target.value })}
                      className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Duration (min)</label>
                    <select
                      value={slotForm.duration}
                      onChange={(e) => setSlotForm({ ...slotForm, duration: parseInt(e.target.value) })}
                      className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none"
                    >
                      <option value={30}>30 minutes</option>
                      <option value={45}>45 minutes</option>
                      <option value={60}>60 minutes</option>
                      <option value={90}>90 minutes</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Max Students</label>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={slotForm.max_students}
                      onChange={(e) => setSlotForm({ ...slotForm, max_students: parseInt(e.target.value) })}
                      className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Price ($)</label>
                  <input
                    type="number"
                    min="0"
                    value={slotForm.price}
                    onChange={(e) => setSlotForm({ ...slotForm, price: parseFloat(e.target.value) })}
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Instrument (Optional)</label>
                  <select
                    value={slotForm.instrument}
                    onChange={(e) => setSlotForm({ ...slotForm, instrument: e.target.value })}
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none"
                  >
                    <option value="">Any Instrument</option>
                    {instruments.map((inst) => (
                      <option key={inst.id} value={inst.name}>{inst.name}</option>
                    ))}
                  </select>
                </div>
                
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setShowCreateSlot(false)}
                    className="flex-1 py-2 bg-stone-700 rounded-lg hover:bg-stone-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateSlot}
                    className="flex-1 py-2 bg-primary text-black font-bold rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Create Slot
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Slot Modal */}
      <AnimatePresence>
        {showEditSlot && editingSlot && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
            onClick={() => { setShowEditSlot(false); setEditingSlot(null); }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-stone-800 rounded-2xl p-6 max-w-md w-full border border-stone-700"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <Edit className="w-5 h-5 text-amber-500" />
                  Edit Lesson Slot
                </h3>
                <button onClick={() => { setShowEditSlot(false); setEditingSlot(null); }} className="p-1 hover:bg-stone-700 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Date</label>
                    <input
                      type="date"
                      value={slotForm.date}
                      onChange={(e) => setSlotForm({ ...slotForm, date: e.target.value })}
                      className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Time</label>
                    <input
                      type="time"
                      value={slotForm.time}
                      onChange={(e) => setSlotForm({ ...slotForm, time: e.target.value })}
                      className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Duration (min)</label>
                    <select
                      value={slotForm.duration}
                      onChange={(e) => setSlotForm({ ...slotForm, duration: parseInt(e.target.value) })}
                      className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none"
                    >
                      <option value={30}>30 minutes</option>
                      <option value={45}>45 minutes</option>
                      <option value={60}>60 minutes</option>
                      <option value={90}>90 minutes</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Max Students</label>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={slotForm.max_students}
                      onChange={(e) => setSlotForm({ ...slotForm, max_students: parseInt(e.target.value) })}
                      className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Price ($)</label>
                  <input
                    type="number"
                    min="0"
                    value={slotForm.price}
                    onChange={(e) => setSlotForm({ ...slotForm, price: parseFloat(e.target.value) })}
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Instrument (Optional)</label>
                  <select
                    value={slotForm.instrument}
                    onChange={(e) => setSlotForm({ ...slotForm, instrument: e.target.value })}
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none"
                  >
                    <option value="">Any Instrument</option>
                    {instruments.map((inst) => (
                      <option key={inst.id} value={inst.name}>{inst.name}</option>
                    ))}
                  </select>
                </div>
                
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => { setShowEditSlot(false); setEditingSlot(null); }}
                    className="flex-1 py-2 bg-stone-700 rounded-lg hover:bg-stone-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateSlot}
                    className="flex-1 py-2 bg-amber-500 text-black font-bold rounded-lg hover:bg-amber-600 transition-colors flex items-center justify-center gap-2"
                  >
                    <Check className="w-4 h-4" />
                    Save Changes
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Instrument Modal */}
      <AnimatePresence>
        {showEditInstrument && editingInstrument && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
            onClick={() => { setShowEditInstrument(false); setEditingInstrument(null); }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-stone-800 rounded-2xl p-6 max-w-md w-full border border-stone-700"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <Music className="w-5 h-5 text-primary" />
                  Edit Instrument
                </h3>
                <button onClick={() => { setShowEditInstrument(false); setEditingInstrument(null); }} className="p-1 hover:bg-stone-700 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                {/* Preview */}
                <div className="flex justify-center">
                  <img
                    src={instrumentForm.image}
                    alt={instrumentForm.name}
                    className="w-24 h-24 object-cover rounded-xl border-2 border-primary"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/96x96?text=Preview';
                    }}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Instrument Name</label>
                  <input
                    type="text"
                    value={instrumentForm.name}
                    onChange={(e) => setInstrumentForm({ ...instrumentForm, name: e.target.value })}
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Select Image</label>
                  
                  {/* Photo Gallery Browser */}
                  <p className="text-xs text-muted-foreground mb-2">Click a photo to select it:</p>
                  <div className="max-h-48 overflow-y-auto bg-stone-900/50 rounded-lg p-2 border border-stone-600">
                    <div className="grid grid-cols-4 gap-2">
                      {availablePhotos.map((photo, idx) => (
                        <button
                          key={idx}
                          onClick={() => setInstrumentForm({ ...instrumentForm, image: photo.url })}
                          className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                            instrumentForm.image === photo.url 
                              ? 'border-primary ring-2 ring-primary/50' 
                              : 'border-transparent hover:border-stone-500'
                          }`}
                        >
                          <img
                            src={photo.url}
                            alt={photo.name}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          {instrumentForm.image === photo.url && (
                            <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                              <Check className="w-5 h-5 text-primary" />
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Manual URL Input (collapsed) */}
                  <details className="mt-3">
                    <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                      Or paste image URL manually...
                    </summary>
                    <input
                      type="text"
                      value={instrumentForm.image}
                      onChange={(e) => setInstrumentForm({ ...instrumentForm, image: e.target.value })}
                      placeholder="https://..."
                      className="w-full mt-2 px-3 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none text-xs"
                    />
                  </details>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <textarea
                    value={instrumentForm.description}
                    onChange={(e) => setInstrumentForm({ ...instrumentForm, description: e.target.value })}
                    placeholder="Describe this instrument and its role in samba music..."
                    rows={3}
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none text-sm resize-none"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    This will show on the Live Lessons booking page
                  </p>
                </div>
                
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => handleDeleteInstrument(editingInstrument.id)}
                    className="py-2 px-4 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => { setShowEditInstrument(false); setEditingInstrument(null); }}
                    className="flex-1 py-2 bg-stone-700 rounded-lg hover:bg-stone-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateInstrument}
                    className="flex-1 py-2 bg-primary text-black font-bold rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
                  >
                    <Check className="w-4 h-4" />
                    Save
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Instrument Modal */}
      <AnimatePresence>
        {showAddInstrument && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
            onClick={() => setShowAddInstrument(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-stone-800 rounded-2xl p-6 max-w-md w-full border border-stone-700"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <Plus className="w-5 h-5 text-green-500" />
                  Add Instrument
                </h3>
                <button onClick={() => setShowAddInstrument(false)} className="p-1 hover:bg-stone-700 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                {/* Preview */}
                {instrumentForm.image && (
                  <div className="flex justify-center">
                    <img
                      src={instrumentForm.image}
                      alt="Preview"
                      className="w-24 h-24 object-cover rounded-xl border-2 border-green-500"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/96x96?text=Preview';
                      }}
                    />
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium mb-1">Instrument Name</label>
                  <input
                    type="text"
                    value={instrumentForm.name}
                    onChange={(e) => setInstrumentForm({ 
                      ...instrumentForm, 
                      name: e.target.value,
                      id: e.target.value.toLowerCase().replace(/\s+/g, '-')
                    })}
                    placeholder="e.g., Berimbau"
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Select Image</label>
                  
                  {/* Photo Gallery Browser */}
                  <p className="text-xs text-muted-foreground mb-2">Click a photo to select it:</p>
                  <div className="max-h-40 overflow-y-auto bg-stone-900/50 rounded-lg p-2 border border-stone-600">
                    <div className="grid grid-cols-4 gap-2">
                      {availablePhotos.map((photo, idx) => (
                        <button
                          key={idx}
                          onClick={() => setInstrumentForm({ ...instrumentForm, image: photo.url })}
                          className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                            instrumentForm.image === photo.url 
                              ? 'border-green-500 ring-2 ring-green-500/50' 
                              : 'border-transparent hover:border-stone-500'
                          }`}
                        >
                          <img
                            src={photo.url}
                            alt={photo.name}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          {instrumentForm.image === photo.url && (
                            <div className="absolute inset-0 bg-green-500/20 flex items-center justify-center">
                              <Check className="w-5 h-5 text-green-500" />
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Manual URL Input (collapsed) */}
                  <details className="mt-3">
                    <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                      Or paste image URL manually...
                    </summary>
                    <input
                      type="text"
                      value={instrumentForm.image}
                      onChange={(e) => setInstrumentForm({ ...instrumentForm, image: e.target.value })}
                      placeholder="https://..."
                      className="w-full mt-2 px-3 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none text-xs"
                    />
                  </details>
                </div>
                
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setShowAddInstrument(false)}
                    className="flex-1 py-2 bg-stone-700 rounded-lg hover:bg-stone-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddInstrument}
                    disabled={!instrumentForm.name || !instrumentForm.image}
                    className="flex-1 py-2 bg-green-500 text-black font-bold rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <Plus className="w-4 h-4" />
                    Add
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
